import React, { useState, useEffect } from 'react';
import { apprenantsAPI, configAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Modal from './Modal';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import Icon from './Icon';

const ModalEdit = ({ isOpen, onClose, apprenant, onUpdate }) => {
  const { isFormateur } = useAuth();
  const [formData, setFormData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salaireMode, setSalaireMode] = useState('exact');
  const [showInsertionFields, setShowInsertionFields] = useState(false);

  useEffect(() => {
    if (apprenant) {
      setFormData({
        nom: apprenant.nom || '',
        prenom: apprenant.prenom || '',
        genre: apprenant.genre || '',
        age: apprenant.age || '',
        telephone: apprenant.telephone || '',
        email: apprenant.email || '',
        ville: apprenant.ville || '',
        diplome: apprenant.diplome || '',
        statutEntree: apprenant.statutEntree || '',
        typeFormation: apprenant.typeFormation || '',
        filiere: apprenant.filiere || '',
        formateur: apprenant.formateur || '',
        dateDebut: apprenant.dateDebut ? new Date(apprenant.dateDebut).toISOString().split('T')[0] : '',
        dateFin: apprenant.dateFin ? new Date(apprenant.dateFin).toISOString().split('T')[0] : '',
        anneeFormation: apprenant.anneeFormation || '',
        dureeFormation: apprenant.dureeFormation || '',
        promotion: apprenant.promotion || '',
        abandon: !!apprenant.abandon,
        estDiplome: !!apprenant.estDiplome,
        statutInsertion: apprenant.statutInsertion || '',
        dateInsertion: apprenant.dateInsertion ? new Date(apprenant.dateInsertion).toISOString().split('T')[0] : '',
        typeContrat: (apprenant.changementsEntreprise?.length > 0
          ? apprenant.changementsEntreprise[apprenant.changementsEntreprise.length - 1].typeContrat
          : apprenant.typeContrat) || '',
        entreprise: (apprenant.changementsEntreprise?.length > 0
          ? apprenant.changementsEntreprise[apprenant.changementsEntreprise.length - 1].entreprise
          : apprenant.entreprise) || '',
        poste: (apprenant.changementsEntreprise?.length > 0
          ? (apprenant.changementsEntreprise[apprenant.changementsEntreprise.length - 1].nouveauPoste || apprenant.poste)
          : apprenant.poste) || '',
        salaire: apprenant.salaire || '',
        salaireTranche: apprenant.salaireTranche || '',
        notes: apprenant.notes || ''
      });
      setSalaireMode(apprenant.salaire ? 'exact' : 'tranche');
      const positiveStatuses = config?.statutsInsertion?.positif || [];
      setShowInsertionFields(positiveStatuses.includes(apprenant.statutInsertion));
    }
    fetchConfig();
  }, [apprenant]);

  const fetchConfig = async () => {
    try {
      const response = await configAPI.get();
      setConfig(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la configuration');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'statutInsertion') {
      const positiveStatuses = config?.statutsInsertion?.positif || [];
      setShowInsertionFields(positiveStatuses.includes(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormateur) {
      toast.error('Pas de droits');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        salaire: salaireMode === 'exact' ? (formData.salaire ? parseInt(formData.salaire) : null) : null,
        salaireTranche: salaireMode === 'tranche' ? formData.salaireTranche : null
      };

      await apprenantsAPI.update(apprenant._id, dataToSend);
      toast.success('Apprenant modifié !');
      onUpdate && onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  if (!formData || !config) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="✏️ Modifier" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6 max-h-[80vh] overflow-y-auto">
        {/* Informations Personnelles */}
        <div className="border-l-4 border-red-600 pl-4 bg-red-50 rounded-r-lg py-4 pr-4">
          <h3 className="font-bold text-red-800 mb-4">👤 Informations Personnelles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormInput label="Nom complet" name="nom" value={formData.nom} onChange={handleChange} required />
            <FormInput label="Prénom" name="prenom" value={formData.prenom} onChange={handleChange} required />  {/* ← ajouter */}
            <FormSelect label="Genre" name="genre" value={formData.genre} onChange={handleChange} required options={['Homme', 'Femme']} />
            <FormInput label="Âge" name="age" type="number" min="15" max="70" value={formData.age} onChange={handleChange} required />
            <FormInput label="Téléphone" name="telephone" type="tel" value={formData.telephone} onChange={handleChange} />
            <FormInput label="Email" name="email" type="email" value={formData.email} onChange={handleChange} />
            <FormInput label="Ville" name="ville" value={formData.ville} onChange={handleChange} />
          </div>
        </div>

        {/* Profil Entrée */}
        <div className="border-l-4 border-purple-600 pl-4 bg-purple-50 rounded-r-lg py-4 pr-4">
          <h3 className="font-bold text-purple-800 mb-4 flex items-center">
            <Icon name="document" size={20} className="mr-2" />
            Profil Entrée
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSelect label="Diplôme" name="diplome" value={formData.diplome} onChange={handleChange} options={config.diplomes || []} />
            <FormSelect label="Statut entrée" name="statutEntree" value={formData.statutEntree} onChange={handleChange} required options={config.statutsEntree || []} />
          </div>
        </div>

        {/* Formation */}
        <div className="border-l-4 border-blue-600 pl-4 bg-blue-50 rounded-r-lg py-4 pr-4">
          <h3 className="font-bold text-blue-800 mb-4 flex items-center">
            <Icon name="book" size={20} className="mr-2" />
            Formation
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormSelect label="Type" name="typeFormation" value={formData.typeFormation} onChange={handleChange} required options={config.typesFormation || []} />
            <FormSelect label="Filière" name="filiere" value={formData.filiere} onChange={handleChange} required options={config.filieres || []} />
            <FormInput label="Formateur" name="formateur" value={formData.formateur} onChange={handleChange} />
            <FormInput label="Date début" name="dateDebut" type="date" value={formData.dateDebut} onChange={handleChange} />
            <FormInput label="Date fin" name="dateFin" type="date" value={formData.dateFin} onChange={handleChange} />
            <FormInput label="Année de formation" name="anneeFormation" value={formData.anneeFormation} onChange={handleChange} placeholder="Ex: 2025" required />
            <FormInput label="Durée de formation" name="dureeFormation" value={formData.dureeFormation} onChange={handleChange} placeholder="Ex: 3 mois" required />
            <FormSelect label="Promotion" name="promotion" value={formData.promotion} onChange={handleChange} options={config.promotions || []} />
            <div className="flex flex-col justify-center gap-2">
              <label htmlFor="abandon" className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  id="abandon"
                  name="abandon"
                  type="checkbox"
                  checked={!!formData.abandon}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    abandon: e.target.checked,
                    ...(e.target.checked ? { estDiplome: false } : {})
                  }))}
                />
                Abandon (a quitté la formation)
              </label>
              <label htmlFor="estDiplome" className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  id="estDiplome"
                  name="estDiplome"
                  type="checkbox"
                  checked={!!formData.estDiplome}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    estDiplome: e.target.checked,
                    ...(e.target.checked ? { abandon: false } : {})
                  }))}
                />
                Diplômé (formation terminée, diplôme obtenu)
              </label>
            </div>
          </div>
        </div>

        {/* Insertion Professionnelle */}
        <div className="border-l-4 border-green-600 pl-4 bg-green-50 rounded-r-lg py-4 pr-4">
          <h3 className="font-bold text-green-800 mb-4 flex items-center">
            <Icon name="briefcase" size={20} className="mr-2" />
            Insertion Professionnelle
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormSelect
              label="Statut"
              name="statutInsertion"
              value={formData.statutInsertion}
              onChange={handleChange}
              options={[
                ...(config.statutsInsertion?.positif || []),
                ...(config.statutsInsertion?.negatif || [])
              ]}
            />
            {showInsertionFields && (
              <>
                <FormInput label="Date insertion" name="dateInsertion" type="date" value={formData.dateInsertion} onChange={handleChange} />
                <FormSelect label="Type contrat" name="typeContrat" value={formData.typeContrat} onChange={handleChange} options={config.typesContrat || []} />
                <FormInput label="Entreprise" name="entreprise" value={formData.entreprise} onChange={handleChange} />
                <FormInput label="Poste" name="poste" value={formData.poste} onChange={handleChange} />
                <div>
                  <label htmlFor="edit-salaire-mode" className="block text-sm font-medium mb-1">Salaire</label>
                  <div className="flex gap-2 mb-2" role="radiogroup" aria-labelledby="edit-salaire-mode-label">
                    <span id="edit-salaire-mode-label" className="sr-only">Mode de saisie du salaire</span>
                    <label htmlFor="salaire-mode-exact" className="flex items-center">
                      <input
                        id="salaire-mode-exact"
                        type="radio"
                        name="salaireMode"
                        value="exact"
                        checked={salaireMode === 'exact'}
                        onChange={() => setSalaireMode('exact')}
                      />
                      <span className="ml-1 text-sm">Montant</span>
                    </label>
                    <label htmlFor="salaire-mode-tranche" className="flex items-center">
                      <input
                        id="salaire-mode-tranche"
                        type="radio"
                        name="salaireMode"
                        value="tranche"
                        checked={salaireMode === 'tranche'}
                        onChange={() => setSalaireMode('tranche')}
                      />
                      <span className="ml-1 text-sm">Tranche</span>
                    </label>
                  </div>
                  {salaireMode === 'exact' ? (
                    <FormInput label="Montant salaire" type="number" name="salaire" value={formData.salaire} onChange={handleChange} placeholder="Montant en FCFA" />
                  ) : (
                    <FormSelect label="Tranche salariale" name="salaireTranche" value={formData.salaireTranche} onChange={handleChange} options={config.tranchesSalariales || []} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <FormInput label="Notes" name="notes" value={formData.notes} onChange={handleChange} type="textarea" rows="2" />

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">
            Annuler
          </button>
          <button type="submit" disabled={loading} className="px-6 py-2 bg-red-600 text-white rounded-lg font-semibold disabled:opacity-50 hover:bg-red-700">
            {loading ? '⏳ Enregistrement...' : '💾 Enregistrer'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ModalEdit;

